import { useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  AllDoneFill,
  ArrowBackOutline,
  EditOutline,
  FileTextOutline,
  GlobeOutline,
  ImageOutline,
} from '@lyearn/icons';
import { Button, Divider, Loader, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import CustomerStoryCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/CustomerStoryCardGrid';
import PersonaJourneyCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/PersonaJourneyCardGrid';
import SolutionCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/SolutionCardGrid';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  ContentApprovalStatusEnum,
  KnowledgeAssetContentType,
  ResourceStatusEnum,
  UserSurveyResponseVersionStatusEnum,
} from '@/types/schema';

import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import EmptyState from '../components/EmptyState';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import useApproveContent from '../hooks/useApproveContent';
import { getResponseByQuestionText } from '../SolutionDetails/utils';
import { DetailsPageProps } from '../types';

const SalesConversationDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const { isMobile } = useScreen();
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();

  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const isDraft =
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Draft;

  const { onApprove, fetching: isApproving } = useApproveContent({ contentId: assetId });

  const showApproveButton =
    (personaData?.content as KnowledgeAssetContentType)?.approvalStatus ===
      ContentApprovalStatusEnum.InReview &&
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Published;

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions?.map(
          (question) => question?.questionId || '',
        ) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = getResponseByQuestionText(
    'Sales Conversation name',
    questionsData,
    personaDetailsData,
  );
  const description = getResponseByQuestionText('Description', questionsData, personaDetailsData);

  // Get persona journey IDs and fetch journey data
  const personaJourney = getResponseByQuestionText(
    'Solution Journey',
    questionsData,
    personaDetailsData,
  );
  const journeyIds =
    personaJourney
      ?.split('|')
      .filter(Boolean)
      .flatMap((id) => id.split(',').map((i) => i.trim()))
      .filter(Boolean) || [];

  const [{ data: journeyData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: journeyIds,
      },
    },
    pause: !journeyIds.length,
    requestPolicy: 'cache-and-network',
  });

  const journeyPersonas = (journeyData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetContentType;
  }>;

  // Get customer story IDs and fetch story data
  const customerStoryResponse = getResponseByQuestionText(
    'Customer Story',
    questionsData,
    personaDetailsData,
  );
  const customerStoryIds =
    customerStoryResponse
      ?.split('|')
      .filter(Boolean)
      .flatMap((id) => id.split(',').map((i) => i.trim()))
      .filter(Boolean) || [];

  const [{ data: customerStoryData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: customerStoryIds,
      },
    },
    pause: !customerStoryIds.length,
    requestPolicy: 'cache-and-network',
  });

  const customerStories = (customerStoryData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetContentType;
  }>;

  // Get solution IDs and fetch solution data
  const solutionResponse = getResponseByQuestionText('Solution', questionsData, personaDetailsData);
  const solutionIds =
    solutionResponse
      ?.split('|')
      .filter(Boolean)
      .flatMap((id) => id.split(',').map((i) => i.trim()))
      .filter(Boolean) || [];

  // Get pain points data
  const painPointsResponse = getResponseByQuestionText(
    'Pain Points',
    questionsData,
    personaDetailsData,
  );
  const painPoints =
    painPointsResponse
      ?.split('|')
      .map((point) => point.trim())
      .filter(Boolean) || [];

  const [{ data: solutionsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: solutionIds,
      },
    },
    pause: !solutionIds.length,
    requestPolicy: 'cache-and-network',
  });

  const solutions = (solutionsData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetContentType;
  }>;

  // Get image and links data
  const imageResponse = getResponseByQuestionText(
    'Image Of Conversation',
    questionsData,
    personaDetailsData,
  );
  const imageArray = imageResponse?.split('|').filter(Boolean) || [];
  const mainImage = imageArray[0];
  const additionalImagesCount = imageArray.length - 1;

  const linksResponse = getResponseByQuestionText(
    'Link To Conversation Document',
    questionsData,
    personaDetailsData,
  );
  const links = linksResponse?.split('|').filter(Boolean) || [];

  const documentsResponse = getResponseByQuestionText(
    'Document Of Conversation',
    questionsData,
    personaDetailsData,
  );
  const documents = documentsResponse?.split('|').filter(Boolean) || [];

  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/sales-conversation/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/sales-conversation`);
  }, [navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Stack direction="row" gapX={8}>
          {showApproveButton ? (
            <Button
              StartIcon={AllDoneFill}
              loading={isApproving}
              variant="outlined"
              onClick={onApprove}>
              Approve
            </Button>
          ) : null}
          <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
            Edit Customer Conversation
          </Button>
        </Stack>
      ),
    showHeader: !isMobile,
  });

  const onbackPress = useCallback(() => {
    // navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);

  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  return (
    <Stack className="w-full" justifyContent="center">
      <Stack
        className="max-w-[101.2rem] min-w-[75rem] mx-64 mt-48 mb-128 xs:min-w-full xs:p-12 xs:mt-12"
        direction="col"
        gapY={21}>
        {isAdminRoute ? null : (
          <Stack className="w-full">
            <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
          </Stack>
        )}
        <Stack direction="col" gapY={8}>
          <Typography variant="display-xl">{pageTitle}</Typography>
          <Typography className="text-text-secondary" variant="body-short-02">
            {description}
          </Typography>
        </Stack>
        <Divider />

        {/* Pain Points section - always show with EmptyState if no data */}
        <Typography variant="display-s">Pain Points</Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack direction="col" gapY={8}>
              {painPoints.length > 0 ? (
                painPoints.map((point, index) => (
                  <Typography key={index} className="text-text-secondary" variant="body-long-01">
                    • {point}
                  </Typography>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Stack>

        {/* Solutions section - only show if there are solutions */}
        {solutions.length > 0 && (
          <Stack direction="col" gapY={16}>
            <Typography variant="display-s">Solutions</Typography>
            <Stack
              className="grid grid-cols-2 gap-16 xs:grid-cols-1"
              style={{ maxWidth: 'fit-content' }}>
              {solutions.map((solution, index) => (
                <SolutionCardGrid
                  key={index}
                  hideActionMenu
                  index={index}
                  solution={solution.node as ContentsFragment}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {/* Solution Journey section - only show if there are journeys */}
        {journeyPersonas.length > 0 && isAdminRoute && (
          <>
            <Typography variant="display-s">Solution Journey</Typography>
            <Stack
              className="grid grid-cols-2 gap-16 xs:grid-cols-1"
              style={{ maxWidth: 'fit-content' }}>
              {journeyPersonas.map((journey, index) => (
                <PersonaJourneyCardGrid
                  key={index}
                  hideActionMenu
                  index={index}
                  personaJourney={journey.node as ContentsFragment}
                />
              ))}
            </Stack>
          </>
        )}

        {/* Customer Story section - only show if there are stories */}
        {customerStories.length > 0 && (
          <Stack direction="col" gapY={16}>
            <Typography variant="display-s">Customer Story</Typography>
            <Stack
              className="grid grid-cols-2 gap-16 xs:grid-cols-1"
              style={{ maxWidth: 'fit-content' }}>
              {customerStories.map((story, index) => (
                <CustomerStoryCardGrid
                  key={index}
                  hideActionMenu
                  customerStory={story.node as ContentsFragment}
                  index={index}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {/* Image & Documentations section - always show with EmptyState if no data */}
        <Typography variant="display-s">Image & Documentations</Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
          {/* Image section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" direction="row" gapX={12}>
              <Stack alignItems="center" direction="row" gapX={8}>
                <ImageOutline fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">
                  Image{additionalImagesCount > 0 ? ` +${additionalImagesCount}` : ''}
                </Typography>
              </Stack>
              {additionalImagesCount > 0 && (
                <Typography
                  className="cursor-pointer text-text-link hover:underline ml-auto"
                  variant="body-short-02">
                  View all
                </Typography>
              )}
            </Stack>
            {mainImage ? (
              <Image
                alt="Journey"
                className="w-full h-[200px] rounded-12 bg-bg-secondary"
                height={200}
                src={mainImage}
                style={{ objectFit: 'contain', backgroundColor: 'var(--bg-secondary)' }}
                width={400}
              />
            ) : (
              <EmptyState />
            )}
          </Stack>

          {/* Links section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" gapX={8}>
              <GlobeOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Links</Typography>
            </Stack>
            <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
              {links.length > 0 ? (
                links.map((link, index) => (
                  <Stack
                    key={index}
                    className={`cursor-pointer group pb-12 mb-12 w-full ${
                      index !== links.length - 1 ? 'border-b border-border' : ''
                    }`}
                    onClick={() => window.open(link, '_blank')}>
                    <Typography
                      className="text-link-external group-hover:text-link-external-hover"
                      variant="body-long-01">
                      {link}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>

          {/* Documents section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" gapX={8}>
              <FileTextOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Documents</Typography>
            </Stack>
            <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
              {documents.length > 0 ? (
                documents.map((doc, index) => (
                  <Stack
                    key={index}
                    className={`cursor-pointer group h-[48px] pb-12 mb-12 w-full ${
                      index !== documents.length - 1 ? 'border-b border-border' : ''
                    }`}
                    onClick={() => window.open(doc, '_blank')}>
                    <Stack alignItems="center" gapX={12}>
                      <FileTextOutline fillColorToken="icon-secondary" height={20} width={20} />
                      <Typography
                        className="text-link-external group-hover:text-link-external-hover truncate"
                        title={doc}
                        variant="body-long-01">
                        Document {index + 1}
                      </Typography>
                    </Stack>
                  </Stack>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SalesConversationDetails;
